import { useEffect } from 'react'
import { createRoot } from 'react-dom/client'
import App from './App.tsx'
import './index.css'
import { isRoutePrefetching } from './lib/routePrefetch'
import { startApp } from './lib/staticHandoff'

// Resilience against stale chunks after a deploy: when a user has the SPA open
// and we ship a new build, their cached index references old lazy-route chunk
// filenames that no longer exist → "Failed to fetch dynamically imported module".
// Auto-reload once (guarded by sessionStorage to avoid reload loops) so they
// transparently get the fresh build instead of an error screen.
const RELOAD_FLAG = 'chunk-reload-attempt';
function handleChunkError(reason: unknown) {
  const msg = String((reason as any)?.message || reason || '');
  if (/dynamically imported module|Importing a module script failed|ChunkLoadError|Failed to fetch/i.test(msg)) {
    if (!sessionStorage.getItem(RELOAD_FLAG)) {
      sessionStorage.setItem(RELOAD_FLAG, '1');
      window.location.reload();
    }
  }
}
window.addEventListener('vite:preloadError', (e) => {
  e.preventDefault();
  // A hover prefetch (src/lib/routePrefetch.ts) must not reload the page under
  // the cursor; if the build is stale, the click itself reloads.
  if (isRoutePrefetching()) return;
  handleChunkError((e as any).payload);
});
window.addEventListener('unhandledrejection', (e) => handleChunkError(e.reason));
// Clear the guard once a navigation succeeds (page alive 3s = healthy load)
setTimeout(() => { try { sessionStorage.removeItem(RELOAD_FLAG); } catch {} }, 3000);

// Fires once React has committed the first render (the static-page handoff waits for it).
function Committed({ onCommit }: { onCommit: () => void }) {
  useEffect(onCommit, [onCommit]);
  return null;
}

// Prerendered pages (scripts/prerender.mjs) hand over from their static snapshot;
// everything else renders straight into #root. See src/lib/staticHandoff.ts.
startApp((container, onCommit) => {
  createRoot(container).render(<><App /><Committed onCommit={onCommit} /></>);
});
